/* eslint-disable complexity */
import {
  BusinessInsightSchedule,
  ForecastListItem,
  InsightScheduleCreateRequest,
  InsightScheduleUpdateRequest,
  PerformanceMetricsBusinessInsights,
  PerformanceMetricsBusinessInsightsComparisonData
} from '@futrli-sdk/predict';

import { ApiView, ApiViewState, getDefaultApiViewState } from './core/ApiView';
import {
  businessInsightsMetricsQuery,
  comparisonMetricsQuery,
  comparisonMetricsQueryKey
} from './queries/businessInsightsMetrics';
import { compareModifiedDateNewestFirst } from '../predict/src/dashboard/utils';
import {
  createScheduleMutation,
  deleteAllSchedulesMutation,
  deleteScheduleMutation,
  updateScheduleMutation
} from './mutations/schedules';
import { fetchBusinessInsightsMetrics } from '../predict/src/core/api/performance-metrics';
import { forecastsQuery } from './queries/forecasts';
import { PeriodOption, PreferencesPayload } from '../predict/src/dashboard/types';
import { preferencesQuery } from './queries/preferences';
import { PRIOR_PERIOD_VALUE } from '../predict/src/dashboard/components/performance-overview/show-performance-select';
import { schedulesQuery } from './queries/schedules';
import { updatePreferencesMutation } from './mutations/preferences';

export interface CustomersSuppliersInfo {
  daysNumCustomersPaying: number;
  daysNumPayingToSuppliers: number;
  unpaidCashInFromInvoices: string;
  unpaidCashOutFromBills: string;
}

export interface OrganisationOverviewViewState extends ApiViewState {
  activeSchedule?: BusinessInsightSchedule;
  businessInsightsMetrics?: PerformanceMetricsBusinessInsights;
  comparisonMode: string;
  customersSuppliersInfo: CustomersSuppliersInfo | null;
  errorSavingPreferences: Error | null;
  errorSavingSchedule: Error | null;
  forecasts: ForecastListItem[];
  isComparisonLoading: boolean;
  isInitialLoading: boolean;
  isPriorPeriod: boolean;
  isSavingPreferences: boolean;
  isSavingSchedule: boolean;
  period: string;
  periodData?: PerformanceMetricsBusinessInsightsComparisonData;
  performanceToTargetsPeriodData?: PerformanceMetricsBusinessInsightsComparisonData;
  schedules?: BusinessInsightSchedule[];
  selectedForecast?: ForecastListItem;
}

export class OrganisationOverviewView extends ApiView {
  private businessInsightsMetricsObs: ReturnType<typeof businessInsightsMetricsQuery>;
  private comparisonMetricsObs: ReturnType<typeof comparisonMetricsQuery>;
  private forecastsObs: ReturnType<typeof forecastsQuery>;
  private preferencesObs: ReturnType<typeof preferencesQuery>;
  private schedulesObs: ReturnType<typeof schedulesQuery>;

  private createScheduleMut: ReturnType<typeof createScheduleMutation>;
  private updateScheduleMut: ReturnType<typeof updateScheduleMutation>;
  private deleteScheduleMut: ReturnType<typeof deleteScheduleMutation>;
  private deleteAllSchedulesMut: ReturnType<typeof deleteAllSchedulesMutation>;
  private updatePreferencesMut: ReturnType<typeof updatePreferencesMutation>;

  private _comparisonMode: string = PRIOR_PERIOD_VALUE;
  private _initialized = false;
  private _period: string = PeriodOption.THIS_MONTH;
  private _selectedForecast?: ForecastListItem;

  public state: OrganisationOverviewViewState = {
    ...getDefaultApiViewState(),
    activeSchedule: undefined,
    businessInsightsMetrics: undefined,
    comparisonMode: PRIOR_PERIOD_VALUE,
    customersSuppliersInfo: null,
    errorSavingPreferences: null,
    errorSavingSchedule: null,
    forecasts: [],
    isComparisonLoading: false,
    isInitialLoading: true,
    isPriorPeriod: true,
    isSavingPreferences: false,
    isSavingSchedule: false,
    period: PeriodOption.THIS_MONTH,
    periodData: undefined,
    performanceToTargetsPeriodData: undefined,
    schedules: undefined,
    selectedForecast: undefined
  };

  constructor(
    private workspaceId: string,
    private organisationId: string,
    private userId: string
  ) {
    super();

    // Queries
    this.businessInsightsMetricsObs = this.addQuery(
      businessInsightsMetricsQuery(workspaceId, organisationId)
    );
    this.comparisonMetricsObs = this.addQuery(
      comparisonMetricsQuery(workspaceId, organisationId, '')
    );
    this.forecastsObs = this.addQuery(forecastsQuery(workspaceId, organisationId));
    this.preferencesObs = this.addQuery(preferencesQuery(workspaceId, organisationId, userId));
    this.schedulesObs = this.addQuery(schedulesQuery(workspaceId, organisationId));

    // Mutations
    this.createScheduleMut = this.addMutation(createScheduleMutation(workspaceId, organisationId));
    this.updateScheduleMut = this.addMutation(updateScheduleMutation(workspaceId, organisationId));
    this.deleteScheduleMut = this.addMutation(deleteScheduleMutation(workspaceId, organisationId));
    this.deleteAllSchedulesMut = this.addMutation(deleteAllSchedulesMutation(workspaceId, organisationId));
    this.updatePreferencesMut = this.addMutation(updatePreferencesMutation(workspaceId, organisationId, userId));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public createSchedule(data: Partial<InsightScheduleCreateRequest>) {
    return this.createScheduleMut.mutate(data);
  }

  public deleteAllSchedules() {
    return this.deleteAllSchedulesMut.mutate(undefined as void);
  }

  public deleteSchedule(scheduleId: string) {
    return this.deleteScheduleMut.mutate(scheduleId);
  }

  public setComparisonForecast(forecastId: string | undefined) {
    if (forecastId === this._comparisonMode) {
      return;
    }

    const forecasts = this.forecasts;

    if (!forecastId || forecastId === PRIOR_PERIOD_VALUE) {
      this._comparisonMode = PRIOR_PERIOD_VALUE;
      this._selectedForecast = undefined;
      this.notify();
      this.savePreferences(this._period, undefined);
      return;
    }

    const forecast = forecasts.find(f => f.id === forecastId);
    if (!forecast) {
      return;
    }

    this._selectedForecast = forecast;
    this._comparisonMode = forecastId;

    this.comparisonMetricsObs.setOptions({
      queryKey: comparisonMetricsQueryKey(this.organisationId, this.workspaceId, forecastId),
      queryFn: () =>
        fetchBusinessInsightsMetrics(this.workspaceId, this.organisationId, {
          comparison_forecast_id: forecastId
        }),
      enabled: true
    });

    this.notify();
    this.savePreferences(this._period, forecastId);
  }

  public setPeriod(periodValue: string) {
    if (this._period === periodValue) {
      return;
    }
    this._period = periodValue;
    this.notify();
    this.savePreferences(periodValue, this._selectedForecast?.id);
  }

  public updateSchedule(scheduleId: string, data: Partial<InsightScheduleUpdateRequest>) {
    return this.updateScheduleMut.mutate({ scheduleId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private get activeSchedule(): BusinessInsightSchedule | undefined {
    const schedules = this.schedules;
    if (!schedules) {
      return undefined;
    }
    const forecastId = this._selectedForecast?.id ?? null;
    return schedules.find(s => s.forecast_id === forecastId);
  }

  private get allBusinessInsightsMetrics(): PerformanceMetricsBusinessInsights | undefined {
    return this.getResult(this.businessInsightsMetricsObs).data;
  }

  private get comparisonMetrics(): PerformanceMetricsBusinessInsights | undefined {
    return this.getResult(this.comparisonMetricsObs).data;
  }

  private get customersSuppliersInfo(): CustomersSuppliersInfo | null {
    const metrics = this.allBusinessInsightsMetrics;
    if (!metrics?.invoices_and_bills) {
      return null;
    }
    return {
      daysNumCustomersPaying: metrics.invoices_and_bills.debtor_days,
      daysNumPayingToSuppliers: metrics.invoices_and_bills.creditor_days,
      unpaidCashInFromInvoices: metrics.invoices_and_bills.invoices_due,
      unpaidCashOutFromBills: metrics.invoices_and_bills.bills_due
    };
  }

  private get errorSavingPreferences(): Error | null {
    return this.updatePreferencesMut.getCurrentResult().error;
  }

  private get errorSavingSchedule(): Error | null {
    return (
      this.createScheduleMut.getCurrentResult().error ??
      this.updateScheduleMut.getCurrentResult().error ??
      this.deleteScheduleMut.getCurrentResult().error ??
      this.deleteAllSchedulesMut.getCurrentResult().error
    );
  }

  private get isComparisonLoading(): boolean {
    return this.getResult(this.comparisonMetricsObs).isLoading;
  }

  private get isInitialLoading(): boolean {
    return (
      this.getResult(this.businessInsightsMetricsObs).isLoading ||
      this.getResult(this.forecastsObs).isLoading ||
      this.getResult(this.preferencesObs).isLoading ||
      this.getResult(this.schedulesObs).isLoading
    );
  }

  private get forecasts(): ForecastListItem[] {
    const raw = this.getResult(this.forecastsObs).data;
    if (!raw) {
      return [];
    }
    return raw
      .filter(f => (f.kind === 'live' ? !f.is_hidden : true))
      .sort(compareModifiedDateNewestFirst);
  }

  protected getNewState(): OrganisationOverviewViewState {
    this.initializeFromPreferences();

    return {
      ...super.getNewState(),
      activeSchedule: this.activeSchedule,
      businessInsightsMetrics: this.allBusinessInsightsMetrics,
      comparisonMode: this._comparisonMode,
      customersSuppliersInfo: this.customersSuppliersInfo,
      errorSavingPreferences: this.errorSavingPreferences,
      errorSavingSchedule: this.errorSavingSchedule,
      forecasts: this.forecasts,
      isComparisonLoading: this.isComparisonLoading,
      isInitialLoading: this.isInitialLoading,
      isPriorPeriod: this._comparisonMode === PRIOR_PERIOD_VALUE,
      isSavingPreferences: this.isSavingPreferences,
      isSavingSchedule: this.isSavingSchedule,
      period: this._period,
      periodData: this.periodData,
      performanceToTargetsPeriodData: this.performanceToTargetsPeriodData,
      schedules: this.schedules,
      selectedForecast: this._selectedForecast
    };
  }

  private initializeFromPreferences() {
    if (this._initialized) {
      return;
    }

    const preferences = this.getResult(this.preferencesObs).data;
    const forecasts = this.forecasts;

    if (!preferences || !forecasts.length) {
      return;
    }

    this._initialized = true;
    const data = preferences.data;

    if (data?.business_insights?.so_far_period) {
      this._period = data.business_insights.so_far_period;
    }

    const savedId = data?.business_insights?.comparison_forecast_id;
    const savedForecast = savedId ? forecasts.find(f => f.id === savedId) : undefined;

    if (savedForecast) {
      this._comparisonMode = savedForecast.id;
      this._selectedForecast = savedForecast;

      this.comparisonMetricsObs.setOptions({
        queryKey: comparisonMetricsQueryKey(this.organisationId, this.workspaceId, savedForecast.id),
        queryFn: () =>
          fetchBusinessInsightsMetrics(this.workspaceId, this.organisationId, {
            comparison_forecast_id: savedForecast.id
          }),
        enabled: true
      });
    }
  }

  private get isSavingPreferences(): boolean {
    return this.updatePreferencesMut.getCurrentResult().status === 'pending';
  }

  private get isSavingSchedule(): boolean {
    return (
      this.createScheduleMut.getCurrentResult().status === 'pending' ||
      this.updateScheduleMut.getCurrentResult().status === 'pending' ||
      this.deleteScheduleMut.getCurrentResult().status === 'pending' ||
      this.deleteAllSchedulesMut.getCurrentResult().status === 'pending'
    );
  }

  private get periodData(): PerformanceMetricsBusinessInsightsComparisonData | undefined {
    const metrics = this.allBusinessInsightsMetrics;
    if (!metrics?.actuals_vs_prior_period) {
      return undefined;
    }
    return metrics.actuals_vs_prior_period[this._period as PeriodOption];
  }

  private get performanceToTargetsPeriodData(): PerformanceMetricsBusinessInsightsComparisonData | undefined {
    const metrics = this.comparisonMetrics;
    if (!metrics?.actuals_vs_forecast) {
      return undefined;
    }
    return metrics.actuals_vs_forecast[this._period as PeriodOption];
  }

  private savePreferences(soFarPeriod: string, comparisonForecastId?: string) {
    const payload: PreferencesPayload = {
      so_far_period: soFarPeriod,
      comparison_forecast_id: comparisonForecastId
    };
    this.updatePreferencesMut.mutate(payload);
  }

  private get schedules(): BusinessInsightSchedule[] | undefined {
    return this.getResult(this.schedulesObs).data?.data;
  }
}
